import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFutureOrderStore } from '../../components/Stores/FutureOrdersStore';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../components/Stores/accountStore';
import ButtonDesigned from '../../components/UI-Elements/Buttons/ButtonDesigned';
import { useDemandPositions } from '../../hooks/useDemandPositions';
import { FutureOrdersSummeryGraphs } from './FutureOrders/FutureOrdersSummeryGraphs/FutureOrdersSummeryGraphs';
import { FutureOrdersTables } from './FutureOrders/FutureOrdersTables/FutureOrdersTables';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import { useExposureStore } from '../../components/Stores/exposureDataStore';
import {
  ADD_FO_FIRST_STEP,
  ADD_FUTURE_ORDER_DEFAULT_ORDER_ID,
  ADD_FUTURE_ORDER_ROOT_ROUTE,
} from './AddNewFutureOrder/AddNewFutureOrderUtils';
import { useUser } from '@hooks/useUser';
import { DemandGrid } from './SalesCommComponents/DemandGrid';
import { useFormulasPrices } from '@hooks/useFormulasPrices';
import { aggregateDemandPositionsToMaterialsDic } from './demandUtils';
import { DemandSummaryGraphs } from './DemandSummaryGraphs';
import { useFx } from '@hooks/useFx';
import { useBaseCurrency } from '@hooks/useBaseCurrency';
import { ExposureHorizonTableBanner } from '../../components/ExposureHorizonTableBanner';
import { getExposureWindow } from '../../utils/exposureHorizon';
import { getDemandTerm } from '../../utils/categoryTerms';
import { splitByHorizon, getUniqueFormulaCodes } from '../../utils/positionUtils';

export interface materialExposureDictionary {
  totalValue: number;
  totalWeight: number;
  exposureCalendar: Record<string, number>;
  price?: number;
}

export const Demand = () => {
  const navigate = useNavigate();
  const { account, formulaColorDictionary: colorsMap } = useAccountStore();
  const { data: user } = useUser();
  const { materials, isMaterialsLoading, materialsMap } = useMaterialsStore();
  const {
    netExposure,

    currentSelectedFormulaId: materialId,
    setCurrentSelectedFormulaId,
  } = useExposureStore();
  const { data: pricesData } = useFormulasPrices();
  const pricesMap = pricesData?.pricesMap || {} as Record<string, { price: number; currency: string }>;
  const priceAsOfDate = pricesData?.asOfDate || null;

  // Use new Postgres API for demand positions
  const { data: demandPositions, isLoading: isDemandLoading, refetch: refetchDemand, error } = useDemandPositions();
  const { resetStore } = useFutureOrderStore();
  const [dataStatus, setDataStatus] = useState<'loading' | 'missing' | 'fetched'>('missing');
  const [showOutOfScope, setShowOutOfScope] = React.useState(false);
  
  // Initialize to 'general' mode if no materialId is set
  useEffect(() => {
    if (!materialId) {
      setCurrentSelectedFormulaId('general');
    }
  }, [materialId, setCurrentSelectedFormulaId]);
  
  // Extract unique formula codes from demand positions
  const allFutureOrdersMaterials = demandPositions 
    ? getUniqueFormulaCodes(demandPositions)
    : [];

  console.log('[Demand] Postgres demand positions:', demandPositions?.length || 0);
  console.log('[Demand] Unique formulas:', allFutureOrdersMaterials);

  // Configure shared formula card bar
  useCardBar({ filterFormulaCodes: allFutureOrdersMaterials });

  useEffect(() => {
    if (isMaterialsLoading || isDemandLoading) {
      setDataStatus('loading');
    } else if (materials.length > 0 && demandPositions && demandPositions.length > 0) {
      setDataStatus('fetched');
    } else {
      setDataStatus('missing');
    }
  }, [isMaterialsLoading, isDemandLoading, materials, demandPositions]);

  // Refetch when account changes
  useEffect(() => {
    if (user?.accountId) {
      console.log('[Demand] Account ID changed to:', user.accountId, '- refetching...');
      refetchDemand();
    }
  }, [user?.accountId, refetchDemand]);

  // Calculate viewing horizon and filter positions
  const horizonMonths = account?.viewingHorizonMonths || 12;
  const exposureWindow = React.useMemo(() => getExposureWindow(horizonMonths), [horizonMonths]);

  const { inScopePositions, outOfScopePositions } = React.useMemo(() => {
    if (!demandPositions) return { inScopePositions: [], outOfScopePositions: [] };
    const { inScope, outOfScope } = splitByHorizon(demandPositions, exposureWindow);
    return { inScopePositions: inScope, outOfScopePositions: outOfScope };
  }, [demandPositions, exposureWindow]);

  // Positions to display based on toggle
  // showOutOfScope=false: show only in-scope
  // showOutOfScope=true: show all (including out-of-scope)
  const displayPositions = React.useMemo(() => {
    return showOutOfScope ? demandPositions : inScopePositions;
  }, [showOutOfScope, demandPositions, inScopePositions]);

  // Get FX rates for currency conversion using account base currency
  const baseCurrency = useBaseCurrency();
  const { getRateToBase } = useFx(baseCurrency);
  
  // Aggregate demand positions for charts using proper FX rates
  // Use displayPositions so charts respect the toggle (consistency with table)
  const demandAggregated = React.useMemo(() => {
    if (!displayPositions || displayPositions.length === 0) return {};
    
    // Convert pricesMap to simple price dictionary (already in USD)
    const priceDic: Record<string, number> = {};
    Object.entries(pricesMap).forEach(([formulaCode, priceInfo]) => {
      priceDic[formulaCode] = priceInfo.price;
    });
    
    return aggregateDemandPositionsToMaterialsDic(
      displayPositions,
      materialId && materialId !== 'general' ? materialId : undefined,
      priceDic,
      getRateToBase,
    );
  }, [displayPositions, materialId, pricesMap, getRateToBase]);

  return (
    <div className="flex flex-col h-full space-y-[1rem]">
      <ExposureHorizonTableBanner
        window={exposureWindow}
        inScopeCount={inScopePositions.length}
        outOfScopeCount={outOfScopePositions.length}
        totalCount={demandPositions?.length || 0}
        showOutOfScope={showOutOfScope}
        onToggleOutOfScope={setShowOutOfScope}
        categoryName={getDemandTerm(account)}
      />
      <div>
        <DemandSummaryGraphs
          componentDataState={dataStatus}
          demandAggregated={demandAggregated}
        />
      </div>
      <div className="flex flex-col flex-1 grow ">
        <DemandGrid
          positions={displayPositions || []}
          formulaId={materialId}
          formulaName={account.formulasToAccount.find(f => f.formulaId === materialId)?.displayName || materialId}
          colorsMap={colorsMap}
          pricesMap={pricesMap}
          priceAsOfDate={priceAsOfDate}
          exposureWindow={exposureWindow}
        />
      </div>
    </div>
  );
};
